import type { ReactNode } from "react";
import {
  Collection as ReactAriaCollection,
  Header as ReactAriaHeader,
  ListBox as ReactAriaListBox,
  ListBoxItem as ReactAriaListBoxItem,
  ListBoxSection as ReactAriaListBoxSection,
  type ListBoxItemProps as ReactAriaListBoxItemProps,
  type ListBoxProps as ReactAriaListBoxProps,
} from "react-aria-components";

import { useI18n } from "../use-i18n";
import { cn, cv, type VariantProps } from "../utils";
import { NcFormattedMessage } from "./nc-formatted-message";
import { NcIconTick } from "./nc-icons";

const ncListBoxListVariants = cv(
  "outline-none data-[orientation='horizontal']:flex data-[orientation='horizontal']:flex-wrap data-[empty='true']:italic data-[empty='true']:p-3",
  {
    variants: {
      variant: {
        default: "",
        borderless: "[&_[role=option]]:border-b-0 [&_[role=option]]:border-b-0",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

const ncListBoxItemVariants = cv(
  cn(
    "group flex justify-start items-center gap-2 px-3 py-3 text-left no-underline transition-colors duration-150",
    "not-last:border-b border-ui",
    "data-selected:text-light data-selected:font-bold data-selected:bg-active z-10",
    "focus-visible:not-data-focus-visible:outline-none data-focus-visible:outline-ui data-focus-visible:outline-2 data-focus-visible:-outline-offset-2 data-focus-visible:text-light",
    "data-hovered:text-light data-hovered:cursor-pointer",
    "aria-disabled:cursor-not-allowed aria-disabled:bg-disabled-light aria-disabled:text-disabled"
  ),
  {
    variants: {
      variant: {
        default: "data-hovered:bg-main data-focus-visible:bg-main",
        success:
          "text-success data-hovered:bg-success data-focus-visible:bg-success data-selected:bg-success",
        danger:
          "text-danger data-hovered:bg-danger data-focus-visible:bg-danger data-selected:bg-danger",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

export const ncListBoxClasses = {
  list: ncListBoxListVariants,
  item: ncListBoxItemVariants,
  icon: "ml-auto hidden flex-shrink-0 leading-none group-data-selected:visible group-data-selected:block",
  header:
    "flex gap-2 justify-start items-center px-3 py-2 bg-ui-light text-dark bg-light border-b group-not-first-of-type:border-t border-ui font-bold sticky top-0",
  section: "not-last:mb-4 group",
};

export interface ListBoxItemProps
  extends ReactAriaListBoxItemProps, VariantProps<typeof ncListBoxItemVariants> {
  children: ReactNode;
  hideSelectIndicator?: boolean;
}

const NcListBoxItem = ({
  children,
  variant,
  textValue,
  hideSelectIndicator = false,
  className,
  ...props
}: ListBoxItemProps) => {
  const childTextValue = typeof children === "string" ? children : undefined;
  return (
    <ReactAriaListBoxItem
      data-nc="NcListBox.Item"
      className={cn(ncListBoxItemVariants({ variant }), className)}
      textValue={textValue || childTextValue}
      {...props}
    >
      {children}
      {!hideSelectIndicator && (
        <div aria-hidden="true" className={ncListBoxClasses.icon}>
          <NcIconTick />
        </div>
      )}
    </ReactAriaListBoxItem>
  );
};

const NcDefaultEmptyState = () => {
  const { t } = useI18n();
  return <NcFormattedMessage variant="secondary">{t("None")}</NcFormattedMessage>;
};

export interface NcListBoxProps<T extends object>
  extends ReactAriaListBoxProps<T>, VariantProps<typeof ncListBoxListVariants> {}

const NcListBox = function <T extends object>({
  renderEmptyState,
  variant,
  className,
  ...props
}: NcListBoxProps<T>) {
  return (
    <ReactAriaListBox
      data-nc="NcListBox"
      className={cn(ncListBoxListVariants({ variant }), className)}
      renderEmptyState={renderEmptyState ? renderEmptyState : () => <NcDefaultEmptyState />}
      {...props}
    />
  );
};

const ListBoxHeader = ({ ...props }) => (
  <ReactAriaHeader className={ncListBoxClasses.header} {...props} />
);
const ListBoxSection = ({ ...props }) => (
  <ReactAriaListBoxSection className={ncListBoxClasses.section} {...props} />
);

NcListBox.Item = NcListBoxItem;
NcListBox.Header = ListBoxHeader;
NcListBox.Section = ListBoxSection;
NcListBox.Collection = ReactAriaCollection;

export { NcListBox };
